interface Dot {
  x: number
  y: number
  baseX: number
  baseY: number
  offsetX: number
  offsetY: number
  waveIntensity: number
  hoverIntensity: number
}

interface Ripple {
  x: number
  y: number
  radius: number
  maxRadius: number
  startTime: number
  duration: number
  intensity: number
  isSecondary: boolean
  hitObstacles: Set<number>
}

interface MirrorSource {
  x: number
  y: number
  decay: number
}

class DotBackground {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private dots: Dot[] = []
  private ripples: Ripple[] = []
  private mouseX = -1000
  private mouseY = -1000
  private animationId: number | null = null

  // Configuration - adjust these values
  private spacing = 20 // Double density (was 40)
  private dotRadius = 1.3
  private dotRadiusMax = 3.5 // Max size during wave for 3D effect
  private sphereRadius = 120
  private sphereStrength = 20
  private waveWidth = 80 // Much thicker wave for visible 3D bulge
  private waveSpeed = 300 // pixels per second
  private waveDuration = 5000 // ms for wave to fully dissipate
  private waveDisplacement = 15 // Stronger displacement for 3D effect

  // Edge bounce (method of images)
  private enableEdgeBounce = false
  private bounceDecay = 0.6 // Intensity multiplier per bounce
  private maxRipples = 5

  // UI element bounce — secondary ripples spawned at element surface on contact
  private enableUIBounce = false
  private uiBounceIntensity = 0.35
  private uiBounceDuration = 2500 // ms — shorter than primary, but fades naturally
  private uiBounceSelectors = ['.viewport-container', '.input-field', '.card']
  private obstacleRects: { left: number; right: number; top: number; bottom: number }[] = []
  private obstacleRectsStale = true

  // Gradient colors (dark grey -> purple -> pink -> white)
  private colorStops = [
    { r: 50, g: 50, b: 60 },    // Dark grey-blue (base)
    { r: 124, g: 58, b: 237 },  // Violet
    { r: 168, g: 85, b: 247 },  // Purple accent
    { r: 236, g: 72, b: 153 },  // Pink accent
    { r: 255, g: 200, b: 220 }  // Light pink (peak)
  ]

  constructor(canvasId: string) {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement
    if (!canvas) {
      throw new Error(`Canvas not found: ${canvasId}`)
    }
    this.canvas = canvas
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Could not get 2D context')
    }
    this.ctx = ctx

    this.init()
  }

  private init(): void {
    this.resize()
    this.createDots()

    // On mobile, draw static dots once and skip animation/interaction
    if (window.innerWidth < 1024) {
      this.drawStatic()
      // Re-draw after page fully loads to get correct document height
      window.addEventListener('load', () => {
        setTimeout(() => {
          this.resize()
          this.createDots()
          this.drawStatic()
        }, 100)
      })
      return
    }

    this.setupEventListeners()
    this.animate()

    // Re-create dots after page fully loads to get correct document height
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.resize()
        this.createDots()
      }, 100)
    })
  }

  private resize(): void {
    const dpr = window.devicePixelRatio || 1
    // Canvas covers full document (absolute position)
    const width = window.innerWidth
    const height = this.getDocumentHeight()

    this.canvas.width = width * dpr
    this.canvas.height = height * dpr

    // Reset transform before scaling (prevents accumulation on repeated calls)
    this.ctx.setTransform(1, 0, 0, 1, 0, 0)
    this.ctx.scale(dpr, dpr)

    this.canvas.style.width = `${width}px`
    this.canvas.style.height = `${height}px`
  }

  private getDocumentHeight(): number {
    return Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      window.innerHeight
    )
  }

  private createDots(): void {
    this.dots = []
    const width = window.innerWidth
    // Use full document height for dots
    const height = this.getDocumentHeight()
    const cols = Math.ceil(width / this.spacing) + 2
    const rows = Math.ceil(height / this.spacing) + 2

    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        const x = i * this.spacing
        const y = j * this.spacing
        this.dots.push({
          x,
          y,
          baseX: x,
          baseY: y,
          offsetX: 0,
          offsetY: 0,
          waveIntensity: 0,
          hoverIntensity: 0
        })
      }
    }
  }

  private setupEventListeners(): void {
    // Mouse move - account for scroll position
    window.addEventListener('mousemove', (e) => {
      this.mouseX = e.clientX
      this.mouseY = e.clientY + window.scrollY
    })

    // Mouse leave
    document.addEventListener('mouseleave', () => {
      this.mouseX = -1000
      this.mouseY = -1000
    })

    // Click for ripple
    window.addEventListener('click', (e) => {
      const x = e.clientX
      const y = e.clientY + window.scrollY
      this.createRipple(x, y)
    })

    // Resize and scroll
    window.addEventListener('resize', () => {
      this.resize()
      this.createDots()
      this.obstacleRectsStale = true
    })

    window.addEventListener('scroll', () => {
      this.obstacleRectsStale = true
    }, { passive: true })

    // Update canvas size when content changes
    const resizeObserver = new ResizeObserver(() => {
      this.resize()
      this.createDots()
      this.obstacleRectsStale = true
    })
    resizeObserver.observe(document.body)
  }

  private createRipple(x: number, y: number): void {
    // Cap simultaneous primary ripples
    const primaryCount = this.ripples.filter(r => !r.isSecondary).length
    if (primaryCount >= this.maxRipples) {
      const idx = this.ripples.findIndex(r => !r.isSecondary)
      if (idx !== -1) this.ripples.splice(idx, 1)
    }

    const width = parseInt(this.canvas.style.width)
    const height = parseInt(this.canvas.style.height)
    const maxRadius = Math.sqrt(width * width + height * height)

    this.ripples.push({
      x,
      y,
      radius: 0,
      maxRadius,
      startTime: performance.now(),
      duration: this.waveDuration,
      intensity: 1.0,
      isSecondary: false,
      hitObstacles: new Set()
    })
  }

  private getMirrorSources(ripple: Ripple): MirrorSource[] {
    if (!this.enableEdgeBounce) return []

    const mirrors: MirrorSource[] = []
    const pageWidth = parseInt(this.canvas.style.width)
    const pageHeight = parseInt(this.canvas.style.height)
    const r = ripple.radius
    const x = ripple.x
    const y = ripple.y
    const d1 = this.bounceDecay
    const d2 = d1 * d1

    // Edge mirrors — only compute when wavefront has reached that edge
    if (r > x) mirrors.push({ x: -x, y, decay: d1 }) // Left
    if (r > pageWidth - x) mirrors.push({ x: 2 * pageWidth - x, y, decay: d1 }) // Right
    if (r > y) mirrors.push({ x, y: -y, decay: d1 }) // Top
    if (r > pageHeight - y) mirrors.push({ x, y: 2 * pageHeight - y, decay: d1 }) // Bottom

    // Corner mirrors (double bounce) — only when wavefront has reached the corner
    if (r > Math.sqrt(x * x + y * y))
      mirrors.push({ x: -x, y: -y, decay: d2 })
    if (r > Math.sqrt((pageWidth - x) ** 2 + y * y))
      mirrors.push({ x: 2 * pageWidth - x, y: -y, decay: d2 })
    if (r > Math.sqrt(x * x + (pageHeight - y) ** 2))
      mirrors.push({ x: -x, y: 2 * pageHeight - y, decay: d2 })
    if (r > Math.sqrt((pageWidth - x) ** 2 + (pageHeight - y) ** 2))
      mirrors.push({ x: 2 * pageWidth - x, y: 2 * pageHeight - y, decay: d2 })

    return mirrors
  }

  private updateObstacleRects(): void {
    this.obstacleRects = []
    const scrollY = window.scrollY
    for (const selector of this.uiBounceSelectors) {
      document.querySelectorAll(selector).forEach(el => {
        const rect = el.getBoundingClientRect()
        // Convert to document coordinates
        this.obstacleRects.push({
          left: rect.left,
          right: rect.right,
          top: rect.top + scrollY,
          bottom: rect.bottom + scrollY
        })
      })
    }
    this.obstacleRectsStale = false
  }

  private checkObstacleHits(ripple: Ripple, now: number): void {
    if (!this.enableUIBounce || ripple.isSecondary) return
    if (this.obstacleRectsStale) this.updateObstacleRects()

    for (let oi = 0; oi < this.obstacleRects.length; oi++) {
      if (ripple.hitObstacles.has(oi)) continue

      const rect = this.obstacleRects[oi]

      // Skip if ripple center is inside the element
      if (ripple.x >= rect.left && ripple.x <= rect.right &&
          ripple.y >= rect.top && ripple.y <= rect.bottom) continue

      // Find closest point on rect surface to ripple center
      const closestX = Math.max(rect.left, Math.min(ripple.x, rect.right))
      const closestY = Math.max(rect.top, Math.min(ripple.y, rect.bottom))
      const dist = Math.sqrt((ripple.x - closestX) ** 2 + (ripple.y - closestY) ** 2)

      // Check if wavefront just reached this element
      if (ripple.radius >= dist) {
        ripple.hitObstacles.add(oi)

        // Spawn a secondary ripple at the surface contact point
        this.ripples.push({
          x: closestX,
          y: closestY,
          radius: 0,
          maxRadius: Infinity,
          startTime: now,
          duration: this.uiBounceDuration,
          intensity: this.uiBounceIntensity,
          isSecondary: true,
          hitObstacles: new Set()
        })
      }
    }
  }

  private applyWaveEffect(
    dot: Dot,
    sourceX: number,
    sourceY: number,
    radius: number,
    startTime: number,
    duration: number,
    now: number,
    intensityScale: number
  ): void {
    const rdx = dot.baseX - sourceX
    const rdy = dot.baseY - sourceY
    const rdist = Math.sqrt(rdx * rdx + rdy * rdy)

    const distFromWave = rdist - radius
    if (Math.abs(distFromWave) >= this.waveWidth) return

    const wavePosition = distFromWave / this.waveWidth
    const waveFactor = Math.cos(wavePosition * Math.PI * 0.5)
    const waveFactor2 = waveFactor * waveFactor

    const elapsed = now - startTime
    const fadeOut = Math.max(0, 1 - elapsed / duration)

    if (rdist > 0) {
      const displacement = waveFactor * fadeOut * this.waveDisplacement * intensityScale
      dot.offsetX += (rdx / rdist) * displacement
      dot.offsetY += (rdy / rdist) * displacement
    }

    dot.waveIntensity = Math.max(dot.waveIntensity, waveFactor2 * fadeOut * intensityScale)
  }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate)
    this.update()
    this.draw()
  }

  private update(): void {
    const now = performance.now()

    // Update ripples
    this.ripples = this.ripples.filter(ripple => {
      const elapsed = now - ripple.startTime
      ripple.radius = (elapsed / 1000) * this.waveSpeed
      return elapsed < ripple.duration
    })

    // Check for UI element hits and spawn secondary ripples at surface
    for (const ripple of this.ripples) {
      this.checkObstacleHits(ripple, now)
    }

    // Pre-compute edge mirror sources (only for primary ripples)
    const rippleMirrors: MirrorSource[][] = this.enableEdgeBounce
      ? this.ripples.map(r => r.isSecondary ? [] : this.getMirrorSources(r))
      : []

    // Update dots
    for (const dot of this.dots) {
      // Reset
      dot.offsetX = 0
      dot.offsetY = 0
      dot.waveIntensity = 0
      dot.hoverIntensity = 0

      // Sphere/hover effect from mouse
      const dx = dot.baseX - this.mouseX
      const dy = dot.baseY - this.mouseY
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist < this.sphereRadius && dist > 0) {
        const factor = 1 - dist / this.sphereRadius
        const smoothFactor = factor * factor * (3 - 2 * factor) // Smoothstep
        const push = smoothFactor * this.sphereStrength
        dot.offsetX = (dx / dist) * push
        dot.offsetY = (dy / dist) * push
        dot.hoverIntensity = smoothFactor
      }

      // Ripple/wave effects
      for (let i = 0; i < this.ripples.length; i++) {
        const ripple = this.ripples[i]

        // Apply wave from source (using ripple's own intensity and duration)
        this.applyWaveEffect(dot, ripple.x, ripple.y, ripple.radius, ripple.startTime, ripple.duration, now, ripple.intensity)

        // Apply edge mirror sources (primary ripples only)
        if (this.enableEdgeBounce && rippleMirrors.length > 0) {
          const mirrors = rippleMirrors[i]
          for (const mirror of mirrors) {
            this.applyWaveEffect(dot, mirror.x, mirror.y, ripple.radius, ripple.startTime, ripple.duration, now, mirror.decay * ripple.intensity)
          }
        }
      }

      // Apply offset
      dot.x = dot.baseX + dot.offsetX
      dot.y = dot.baseY + dot.offsetY
    }
  }

  private lerpColor(
    c1: { r: number; g: number; b: number },
    c2: { r: number; g: number; b: number },
    t: number
  ): { r: number; g: number; b: number } {
    return {
      r: Math.round(c1.r + (c2.r - c1.r) * t),
      g: Math.round(c1.g + (c2.g - c1.g) * t),
      b: Math.round(c1.b + (c2.b - c1.b) * t)
    }
  }

  private getGradientColor(intensity: number): { r: number; g: number; b: number } {
    // Map intensity (0-1) to color stops
    const stops = this.colorStops
    if (intensity <= 0) return stops[0]
    if (intensity >= 1) return stops[stops.length - 1]

    const scaledT = intensity * (stops.length - 1)
    const index = Math.floor(scaledT)
    const t = scaledT - index

    return this.lerpColor(stops[index], stops[Math.min(index + 1, stops.length - 1)], t)
  }

  private drawStatic(): void {
    const width = window.innerWidth
    const height = this.getDocumentHeight()
    this.ctx.clearRect(0, 0, width, height)

    for (const dot of this.dots) {
      this.ctx.fillStyle = `rgba(50, 50, 60, 0.15)`
      this.ctx.beginPath()
      this.ctx.arc(dot.baseX, dot.baseY, this.dotRadius, 0, Math.PI * 2)
      this.ctx.fill()
    }
  }

  private draw(): void {
    const width = window.innerWidth
    const height = this.getDocumentHeight()
    this.ctx.clearRect(0, 0, width, height)

    // Only draw dots that are visible (optimization)
    const scrollY = window.scrollY
    const viewTop = scrollY - 50
    const viewBottom = scrollY + window.innerHeight + 50

    for (const dot of this.dots) {
      // Skip dots outside viewport (with some margin)
      if (dot.y < viewTop || dot.y > viewBottom) continue

      // Combine intensities
      const totalIntensity = Math.min(1, dot.waveIntensity + dot.hoverIntensity * 0.8)

      // Get color from gradient
      const color = this.getGradientColor(totalIntensity)

      // Base alpha + intensity boost
      const baseAlpha = 0.15
      const intensityAlpha = totalIntensity * 0.6
      const alpha = Math.min(0.85, baseAlpha + intensityAlpha)

      // Vary dot size based on intensity for 3D bulge effect
      const sizeBoost = totalIntensity * (this.dotRadiusMax - this.dotRadius)
      const currentRadius = this.dotRadius + sizeBoost

      // Draw at actual position (canvas is absolute, covers full document)
      this.ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`
      this.ctx.beginPath()
      this.ctx.arc(dot.x, dot.y, currentRadius, 0, Math.PI * 2)
      this.ctx.fill()
    }
  }

  public dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
  }
}

export function initDotBackground(): void {
  const canvas = document.getElementById('dot-background')
  if (canvas) {
    new DotBackground('dot-background')
  }
}
